import { Component, OnInit, OnDestroy, inject, HostListener } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute, Routes } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { merge } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { environment } from '@env/environment';
import { UntilDestroy, untilDestroyed } from '@shared';
import { I18NLanguageType, I18nService } from '@app/i18n';
import { SubdomainService } from '@shared/services/subdomain.service';
import { SignalrTargetsEnum, SubdomainTypes } from '@shared/enums';
import { ConfigurationService } from '@ep/shared';
import { WindowService } from '@app/resources/services/window.service';
import { SignalRService } from '@app/resources/services/signalr.service';
import { ApplicationProfileService } from './@shared/services/application-profile.service';
import { ApplicationProfile, Credentials, Resources } from './@shared/models';
import { CredentialsService } from '@shared/services/credentials.service';
import { SessionFreezeService } from '@shared/services/session-freeze.service';
import { MerchantNameVisibilityService } from '@shared/services/merchant-name-visibility.service';

@UntilDestroy()
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly signalRService = inject(SignalRService);
  private readonly credentialsService = inject(CredentialsService);
  private readonly sessionFreezeService = inject(SessionFreezeService);
  private readonly merchantNameVisibilityService = inject(MerchantNameVisibilityService);

  applicationProfile!: ApplicationProfile;

  protected sessionInactive = false;
  protected bannerDismissed = false;
  protected sessionClosed = false;
  private routeTitle?: string;
  private readonly defaultTitle = document.title;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private titleService: Title,
    private translateService: TranslateService,
    private i18nService: I18nService,
    private configurationService: ConfigurationService,
    private subdomainService: SubdomainService,
    private applicationProfileService: ApplicationProfileService,
    private windowService: WindowService
  ) {}

  @HostListener('window:storage', ['$event'])
  onStorageChange(event: StorageEvent): void {
    if (event.key !== Resources.CredentialsKey) {
      return;
    }

    const currentJwt = this.credentialsService.credentials?.jwt;
    // Only guard tabs that currently hold an active session (e.g. not the login page)...
    if (!currentJwt) {
      return;
    }

    // ...and only when that session came from an impersonation. Normal logins are never blocked.
    if (!this.credentialsService.isImpersonation()) {
      return;
    }

    if (this.getJwtFromRawCredentials(event.newValue) !== currentJwt) {
      this.blockSession();
    }
  }

  private blockSession(): void {
    this.sessionInactive = true;
    this.freezeSession();
  }

  private freezeSession(): void {
    this.sessionFreezeService.freeze();
    this.signalRService.disconnect();
  }

  protected dismissBanner(): void {
    this.bannerDismissed = true;
  }

  protected onBlockedClick(): void {
    this.bannerDismissed = false;
  }

  @HostListener('document:visibilitychange')
  @HostListener('window:focus')
  onReturnToPage(): void {
    if (document.visibilityState === 'visible') {
      this.bannerDismissed = false;
    }
  }

  private checkForStaleTab(): void {
    const profile = this.applicationProfileService.getApplicationProfile();
    if (!profile?.Location) {
      return;
    }

    // Only impersonation-derived sessions are subject to the blocking behavior.
    if (!this.credentialsService.isImpersonation()) {
      return;
    }

    const urlLocationId = this.getLocationIdFromUrl(this.router.url);
    // Only location-scoped routes carry an identity to compare against.
    if (urlLocationId === null) {
      return;
    }

    if (urlLocationId !== profile.Location.LocationId) {
      this.sessionClosed = true;
      this.freezeSession();
    }
  }

  private getLocationIdFromUrl(url: string): number | null {
    const firstSegment = url.split(/[?#]/)[0].split('/').filter(Boolean)[0];
    return firstSegment && /^\d+$/.test(firstSegment) ? Number(firstSegment) : null;
  }

  private getJwtFromRawCredentials(rawCredentials: string | null): string | null {
    if (!rawCredentials) {
      return null;
    }

    try {
      const credentials: Credentials = JSON.parse(rawCredentials);
      return credentials?.jwt ?? null;
    } catch {
      return null;
    }
  }

  ngOnInit() {
    this.sessionClosed = this.credentialsService.isImpersonation() && this.sessionFreezeService.wasFrozenBeforeReload();

    if (this.sessionClosed) {
      this.sessionFreezeService.freeze();
    } else {
      this.signalRService.startConnection();
    }

    this.applicationProfileService.applicationProfile$
      .pipe(untilDestroyed(this))
      .subscribe((applicationProfile: ApplicationProfile) => {
        this.applicationProfile = applicationProfile;
        this.setFavicon();
        this.updatePageTitle();
        this.checkForStaleTab();
      });

    this.merchantNameVisibilityService.isVisible$.pipe(untilDestroyed(this)).subscribe(() => this.updatePageTitle());

    // Setup translations
    this.i18nService.init(
      (this.configurationService.defaultLanguage ?? environment.defaultLanguage) as I18NLanguageType,
      (this.configurationService.supportedLanguages ?? environment.supportedLanguages) as I18NLanguageType[]
    );

    const onNavigationEnd = this.router.events.pipe(filter((event) => event instanceof NavigationEnd));

    onNavigationEnd.pipe(untilDestroyed(this)).subscribe(() => this.checkForStaleTab());

    // Change page title on navigation or language change, based on route data
    merge(this.translateService.onLangChange, onNavigationEnd)
      .pipe(
        map(() => {
          let route = this.activatedRoute;
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
        switchMap((route) => route.data),
        untilDestroyed(this)
      )
      .subscribe((event) => {
        const title = event['title'];
        if (title) {
          this.routeTitle = title;
          this.updatePageTitle();
        }
      });

    // Handle unsupported routes
    onNavigationEnd
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        filter((event) => event.url.includes('#') && !this.isValidRoute(event.urlAfterRedirects)),
        untilDestroyed(this)
      )
      .subscribe(() => {
        this.router.navigate(['/not-found']);
      });
  }

  private updatePageTitle(): void {
    const merchantName = this.applicationProfile?.Merchant?.Name;
    const routeTitle = this.routeTitle ? this.translateService.instant(this.routeTitle) : undefined;

    const showMerchantName =
      this.credentialsService.isImpersonation() && !!merchantName && this.merchantNameVisibilityService.isVisible;

    const pageTitle = showMerchantName
      ? [routeTitle, merchantName].filter(Boolean).join(' - ')
      : routeTitle ?? (this.credentialsService.isImpersonation() ? undefined : merchantName);

    this.titleService.setTitle(pageTitle ? this.translateService.instant(pageTitle) : this.defaultTitle);
  }

  private isValidRoute(url: string): boolean {
    return this.isUrlInRoutes(url, this.router.config);
  }

  get isMobile() {
    return this.windowService.isMobileScreen;
  }

  private isUrlInRoutes(url: string, routes: Routes): boolean {
    for (const route of routes) {
      if (route.path === url) {
        return true;
      }

      if (route.children) {
        if (this.isUrlInRoutes(url, route.children)) {
          return true;
        }
      }
    }

    return false;
  }

  private setFavicon(): void {
    const link: HTMLLinkElement | null = document.querySelector('#favicon');
    const domain = this.subdomainService.getDomain();

    var dynamicFavIcon = this.applicationProfile?.PortalTheme?.FavIcon;
    if (dynamicFavIcon) {
      document?.getElementById('favicon')?.setAttribute('href', dynamicFavIcon);
    } else {
      if (link) {
        switch (domain) {
          case SubdomainTypes.EagleProcessing:
            link.href = 'assets/favicons/eagleFavicon.ico';
            break;
          case SubdomainTypes.EnrollAndPay:
            link.href = 'assets/favicons/enrollAndPayFavicon.ico';
            break;
          default:
            link.href = 'assets/favicons/enrollAndPayFavicon.ico';
        }
      }
    }
  }

  ngOnDestroy() {
    this.i18nService.destroy();
  }
}
